import { createFileRoute } from '@tanstack/react-router'
import { useTransactions, useEvent } from '@/lib/api/queries'
import { apiClient } from '@/lib/api/client'
import { useAuth } from '@/lib/auth'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, ChevronUp, ChevronDown, Download } from 'lucide-react'
import { useState, useMemo } from 'react'
import { DateFilter } from '@/components/ui/date-filter'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToServerNaive } from '@/lib/eventTime'
import { cn } from '@/lib/utils'
import { useAutoRefresh } from '@/lib/hooks/use-auto-refresh'
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value'
import { formatDate } from '@/lib/date'
import { useModal } from '@/components/modals/modal-container'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { TransactionTypeBadge } from '@/components/transaction-type-badge'
import { CardBrand } from '@/features/fraud/card-brand'
import { isCardSale } from '@/lib/payment-card'

export const Route = createFileRoute('/_authenticated/events/$eventId/transactions')({
  component: TransactionsPage,
})

type TransactionType = 'all' | 'reload' | 'initialize' | 'voucher' | 'order' | 'transfer' | 'refund'

const typeTabs: { key: TransactionType; label: string }[] = [
  { key: 'all', label: 'All Transactions' },
  { key: 'reload', label: 'Reloads' },
  { key: 'initialize', label: 'Initialize' },
  { key: 'voucher', label: 'Voucher Credit' },
  { key: 'order', label: 'Orders' },
  { key: 'transfer', label: 'Transfers' },
  { key: 'refund', label: 'Refunded' },
]

// Map UI types to API action types
const actionMap: Record<TransactionType, string | undefined> = {
  all: undefined,
  reload: 'addCredit',
  initialize: 'addCredit', // Will filter by amount=0 client-side
  voucher: 'voucherCredit',
  order: 'spendCredit',
  transfer: 'transfer',
  refund: 'refund',
}

function TransactionsPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const { openTransactionModal, openUserModal } = useModal()
  const { token } = useAuth()
  
  // Permission checks
  const canViewTransactions = usePermission('can_view_transactions')
  const canExportTransactions = usePermission('can_export_transactions')
  const canSeeUserInfo = usePermission('can_see_user_info')

  // All hooks must be called before any early returns
  const [activeType, setActiveType] = useState<TransactionType>('all')
  const [isExporting, setIsExporting] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 400)
  const [page, setPage] = useState(1)
  const [ordering, setOrdering] = useState('-timestamp')
  const [pageSize, setPageSize] = useState(20)
  const pageSizeOptions = [20, 50, 100, 200]
  const [dateRange, setDateRange] = useState<{ gte?: string; lte?: string }>({})

  const { data: event } = useEvent(eventIdNum)

  // Event-local range -> server-naive for the list (timestamp gte/lte);
  // date-only start/end for the export endpoint.
  const dateParams = useMemo(() => {
    const tz = event?.timezone || DEFAULT_EVENT_TIMEZONE
    const p: { timestamp__gte?: string; timestamp__lte?: string } = {}
    if (dateRange.gte) p.timestamp__gte = eventLocalToServerNaive(dateRange.gte, tz)
    if (dateRange.lte) p.timestamp__lte = eventLocalToServerNaive(dateRange.lte, tz)
    return p
  }, [dateRange, event?.timezone])
  const exportDateParams = useMemo(() => {
    // Date-only export bounds: event-local CALENDAR date (slice), not a
    // server-tz conversion — otherwise the 23:59 end-of-day rolls forward a
    // day for events west of the server, widening the exported range.
    const p: { start_date?: string; end_date?: string } = {}
    if (dateRange.gte) p.start_date = dateRange.gte.slice(0, 10)
    if (dateRange.lte) p.end_date = dateRange.lte.slice(0, 10)
    return p
  }, [dateRange])

  // Auto-refresh from header toggle
  const { refetchInterval } = useAutoRefresh()

  const { data, isLoading, error } = useTransactions(eventIdNum, {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: debouncedSearch || undefined,
    action: actionMap[activeType],
    // Reloads: addCredit with amount > 0; Initialize: addCredit with amount = 0
    ...(activeType === 'reload' ? { amount__gt: 0 } : {}),
    ...(activeType === 'initialize' ? { amount: 0 } : {}),
    ...dateParams,
    ordering,
  }, { refetchInterval })

  // Permission guard - now safe after all hooks
  if (!canViewTransactions) {
    return <AccessDenied message="You don't have permission to view transactions." />
  }

  const transactions = data?.results || []
  const totalCount = data?.count || 0
  const totalPages = Math.ceil(totalCount / pageSize)

  const handleTypeChange = (type: TransactionType) => {
    setActiveType(type)
    setPage(1)
  }

  const handleOrderingChange = (field: string) => {
    if (ordering === field) {
      setOrdering(`-${field}`)
    } else if (ordering === `-${field}`) {
      setOrdering(field)
    } else {
      setOrdering(`-${field}`)
    }
  }

  const getOrderingIcon = (field: string) => {
    if (ordering === field) {
      return <ChevronUp className="w-4 h-4 inline ml-1" />
    } else if (ordering === `-${field}`) {
      return <ChevronDown className="w-4 h-4 inline ml-1" />
    }
    return null
  }

  const handleExport = async () => {
    if (!token) return
    setIsExporting(true)
    try {
      await apiClient.exportTransactions(token, eventIdNum, {
        search: debouncedSearch || undefined,
        action: actionMap[activeType],
        ...exportDateParams,
        ordering,
      })
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setIsExporting(false)
    }
  }

  const getTypeBadge = (transaction: (typeof transactions)[0]) => (
    <TransactionTypeBadge
      action={transaction.action}
      amount={transaction.amount || 0}
      cardSale={isCardSale(transaction)}
    />
  )

  // Get "From" user/source display
  // Display name with fallbacks — staff/system accounts can have empty
  // first/last names, which would otherwise render as a blank link.
  const userLabel = (u: {
    id: number
    first_name?: string | null
    last_name?: string | null
    email?: string | null
    phone_number?: string | null
  }) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim()
    return name || u.email || u.phone_number || `User #${u.id}`
  }

  const getFromDisplay = (transaction: (typeof transactions)[0]) => {
    const amount = transaction.amount || 0

    if (transaction.action === 'addCredit') {
      if (amount === 0) {
        return 'Initialize User'
      }
      // Check for card payment indicators
      if (transaction.stripe_charge_id === 'CASH') {
        return 'Standalone Reload'
      }
      // The card itself says it was a card payment.
      const card = transaction.payment_card
      if (card) return <CardBrand brand={card.brand} last4={card.last4} wallet={card.wallet} />
      return 'Card Payment'
    }

    if (transaction.action?.toLowerCase().includes('voucher')) {
      return 'Token Voucher'
    }

    if (transaction.from_user) {
      if (!canSeeUserInfo) {
        return <span>********</span>
      }
      return (
        <button
          onClick={() => openUserModal(transaction.from_user!.id, eventIdNum)}
          className="text-blue-600 dark:text-blue-500 hover:underline"
        >
          {userLabel(transaction.from_user)}
        </button>
      )
    }

    if (transaction.user) {
      if (!canSeeUserInfo) {
        return <span>********</span>
      }
      return (
        <button
          onClick={() => openUserModal(transaction.user!.id, eventIdNum)}
          className="text-blue-600 dark:text-blue-500 hover:underline font-medium"
        >
          {userLabel(transaction.user)}
        </button>
      )
    }

    // A card sale at a booth with no guest attached: the card paid.
    if (isCardSale(transaction)) {
      const card = transaction.payment_card
      return card ? <CardBrand brand={card.brand} last4={card.last4} wallet={card.wallet} /> : 'Card'
    }

    return 'System'
  }

  // Get "To" user/destination display
  const getToDisplay = (transaction: (typeof transactions)[0]) => {
    if (transaction.action === 'addCredit' || transaction.action?.toLowerCase().includes('voucher')) {
      // The credited account is `user`; `from_user` is whoever performed
      // the reload (support staff) and must not display as the recipient.
      const targetUser = transaction.user || transaction.from_user
      if (targetUser) {
        if (!canSeeUserInfo) {
          return <span>********</span>
        }
        return (
          <button
            onClick={() => openUserModal(targetUser.id, eventIdNum)}
            className="text-blue-600 dark:text-blue-500 hover:underline"
          >
            {userLabel(targetUser)}
          </button>
        )
      }
    }

    if (transaction.to_user) {
      if (!canSeeUserInfo) {
        return <span>********</span>
      }
      return (
        <button
          onClick={() => openUserModal(transaction.to_user!.id, eventIdNum)}
          className="text-blue-600 dark:text-blue-500 hover:underline"
        >
          {userLabel(transaction.to_user)}
        </button>
      )
    }

    if (transaction.order?.vendor_event?.vendor) {
      return transaction.order.vendor_event.vendor.name
    }

    return 'System'
  }

  const getDisplayAmount = (transaction: (typeof transactions)[0]) => {
    if (transaction.action === 'directPay') {
      const dollars = transaction.fee_breakdown?.total_amount ?? transaction.total_amount ?? 0
      return `$${dollars.toFixed(2)}`
    }
    const amount = Math.round(transaction.amount || 0)
    if (transaction.action === 'refund') {
      return -Math.abs(amount)
    }
    if (transaction.action === 'spendCredit' || transaction.action === 'order') {
      return Math.abs(amount)
    }
    return amount
  }

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8">
      <div className="min-h-[calc(100vh-8rem)] flex flex-col py-8">
        {/* Header */}
        <div className="flex flex-row justify-between items-center mb-3 sm:mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">Transactions</h1>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-neutral-200 dark:border-neutral-800 overflow-x-auto">
          <div className="flex space-x-6 min-w-max">
            {typeTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTypeChange(tab.key)}
                className={cn(
                  'px-1 py-3 text-sm font-medium whitespace-nowrap flex items-center',
                  activeType === tab.key
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-500'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                )}
              >
                {tab.label}
                {activeType === tab.key && totalCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 rounded-full">
                    {totalCount.toLocaleString()}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {/* Search and Export Bar */}
          <div className="flex flex-row sm:flex-row gap-4 items-start sm:items-center">
            <div className="relative w-full sm:grow">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
              </div>
              <Input
                type="text"
                placeholder="Search by Transaction ID, User, Amount, or card last 4"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  setPage(1)
                }}
                className="bg-white dark:bg-[#131313] w-full pl-10 pr-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md text-sm text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <DateFilter
              value={dateRange}
              onChange={(v) => { setDateRange(v); setPage(1) }}
              eventTimeZone={event?.timezone}
              defaultMonth={event?.start_date_time ? new Date(event.start_date_time) : undefined}
              label="Date range"
              className="shrink-0"
            />

            {/* Export Button - only show if user has permission */}
            {canExportTransactions && (
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={isExporting}
                className="inline-flex items-center px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700"
              >
                <Download className="w-4 h-4 mr-2" />
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            )}
          </div>

          {/* Table Container */}
          <div className="w-full overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden border border-neutral-200 dark:border-neutral-800 sm:rounded-lg">
                <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                  <thead className="bg-neutral-50 dark:bg-neutral-900 sticky top-0 z-10">
                    <tr>
                      <th
                        scope="col"
                        onClick={() => handleOrderingChange('id')}
                        className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                      >
                        ID {getOrderingIcon('id')}
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
                      >
                        From
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleOrderingChange('amount')}
                        className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                      >
                        Amount {getOrderingIcon('amount')}
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
                      >
                        To
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleOrderingChange('timestamp')}
                        className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                      >
                        Date & Time {getOrderingIcon('timestamp')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                    {isLoading ? (
                      // Loading skeleton
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          {Array.from({ length: 6 }).map((_, j) => (
                            <td key={j} className="px-6 py-4">
                              <div className="h-4 w-16 bg-neutral-200 dark:bg-neutral-800 rounded" />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : error ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center text-red-500">
                          Failed to load transactions: {error.message}
                        </td>
                      </tr>
                    ) : transactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center text-neutral-500 dark:text-neutral-400">
                          No transactions found
                        </td>
                      </tr>
                    ) : (
                      transactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                          <td className="px-6 py-2 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => openTransactionModal(transaction.id, eventIdNum)}
                              className="text-blue-600 dark:text-blue-500 hover:underline"
                            >
                              {transaction.id}
                            </button>
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400 font-medium">
                            {getFromDisplay(transaction)}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                            {getDisplayAmount(transaction)}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400 font-medium">
                            {getToDisplay(transaction)}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm">
                            {getTypeBadge(transaction)}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                            {formatDate(transaction.timestamp, 'M/d/yyyy hh:mm a')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="bg-white dark:bg-[#131313] px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-200 dark:border-neutral-800 sm:px-6 gap-3">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Showing <span className="font-medium">{transactions.length}</span> of{' '}
                      <span className="font-medium">{totalCount.toLocaleString()}</span> transactions
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">Per page:</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value))
                          setPage(1)
                        }}
                        className="text-sm border border-neutral-200 dark:border-neutral-700 rounded-md px-2 py-1 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {pageSizeOptions.map((size) => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center flex-wrap gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        Prev
                      </Button>
                      <span className="px-3 py-1 text-sm text-neutral-600 dark:text-neutral-400">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
